import Vue from 'vue'
import { get, set } from 'idb-keyval'
import { getFirestore, collection, doc } from 'firebase/firestore'
import { Template, Sheet, Page } from '@/model'

let templatesDirectoryHandle
let templates

const LocalTemplates = {
  install (Vue) {
    Vue.prototype.$localTemplates = {
      get templates () {
        return templates ?? []
      },

      async getDirectoryHandle (templateId) {
        if (templateId && templatesDirectoryHandle) {
          return await templatesDirectoryHandle.getDirectoryHandle(templateId)
        }

        if (templatesDirectoryHandle) {
          return templatesDirectoryHandle
        }

        templatesDirectoryHandle = await get('booko_local_templates_directory')
        if (templatesDirectoryHandle) {
          return templatesDirectoryHandle
        }
      },

      async removeDirectoryHandle () {
        await set('booko_local_templates_directory', null)
        templatesDirectoryHandle = null
      },

      async selectDirectory () {
        templatesDirectoryHandle = await window.showDirectoryPicker({ mode: 'readwrite' })
        if (templatesDirectoryHandle) {
          await set('booko_local_templates_directory', templatesDirectoryHandle)
          return templatesDirectoryHandle
        }
      },

      async getTemplates (requestPermission, loadSheets = true) {
        if (!templatesDirectoryHandle) {
          throw new Error('No template directory selected')
        }

        templates = []

        const hasPermission = await verifyPermission(templatesDirectoryHandle, requestPermission)
        if (hasPermission) {
          for await (const entry of templatesDirectoryHandle.values()) {
            try {
              if (entry.kind === 'directory') {
                const template = await this.getTemplate(entry, loadSheets)
                templates.push(template)
              }
            } catch (e) {
              console.error(e)
            }
          }

          return templates
        }
      },

      async getTemplate (directoryHandle, loadSheets = false) {
        const ref = getRefFromHandle('templates', directoryHandle)
        let sheets = []

        if (loadSheets) {
          const globalLayout = await getTemplateGlobalLayout(directoryHandle)
          const globalStyles = await getTemplateGlobalStyles(directoryHandle)
          sheets = await this.getSheets(directoryHandle)

          sheets.forEach((sheet) => {
            sheet.pages.forEach((page) => {
              page.layout.source = globalLayout + '\n' + page.layout.source
              page.style.source = globalStyles + '\n' + page.style.source
            })
          })
        }

        const data = await getDocumentData(directoryHandle)

        return new Template(ref, data, sheets)
      },

      async getSheets (directoryHandle) {
        const sheetsDirectoryHandle = await directoryHandle.getDirectoryHandle('sheets')
        const sheets = []

        for await (const entry of sheetsDirectoryHandle.values()) {
          try {
            if (entry.kind === 'directory') {
              const sheet = await this.getSheet(entry, directoryHandle)
              sheets.push(sheet)
            }
          } catch (e) {
            console.error(e)
          }
        }

        return sheets
      },

      async getSheet (directoryHandle, parentHandle) {
        const ref = getRefFromHandle(`templates/${parentHandle.name}/sheets`, directoryHandle)
        const data = await getDocumentData(directoryHandle)

        // get pages
        const pagesDirectoryHandle = await parentHandle.getDirectoryHandle('pages')
        data.pages = []
        for (let i = 0; i < data.pagesIds.length; i++) {
          const pageDirectoryHandle = await pagesDirectoryHandle.getDirectoryHandle(data.pagesIds[i])
          const page = await this.getPage(pageDirectoryHandle, parentHandle)
          data.pages.push(page)
        }
        delete data.pagesIds

        return new Sheet(ref, data)
      },

      async getPage (directoryHandle, parentHandle) {
        const ref = getRefFromHandle(`templates/${parentHandle.name}/pages`, directoryHandle)
        const data = await getDocumentData(directoryHandle)

        const layoutFileHandle = await directoryHandle.getFileHandle('layout.html', { create: true })
        const layout = await readFile(layoutFileHandle)
        data.layout = layout

        const styleFileHandle = await directoryHandle.getFileHandle('style.css', { create: true })
        const style = await readFile(styleFileHandle)
        data.style = style

        const scriptFileHandle = await directoryHandle.getFileHandle('script.js', { create: true })
        const script = await readFile(scriptFileHandle)
        data.script = script

        data.images = {}
        for await (const fileHandle of directoryHandle.values()) {
          const file = await fileHandle.getFile()

          if (file.type === 'image/jpeg' || file.type === 'image/png') {
            const imageName = /^([^.]+)/.exec(file.name)[0]
            data.images[imageName] = URL.createObjectURL(file)
          }
        }

        return new Page(ref, data)
      },

      async createTemplate () {
        const db = getFirestore()
        const ref = genRef(db, 'templates')
        const templateDirectoryHandle = await templatesDirectoryHandle.getDirectoryHandle(ref.id, { create: true })
        const data = JSON.stringify({
          available: false,
          name: '',
          author: '',
          description: '',
          tags: [''],
          coverUrl: '',
          printInfo: {
            colorPrint: true,
            paperColor: 'WHI',
            spinSide: 'left',
            format: {
              width: 210,
              height: 297,
              units: 'mm'
            }
          }
        }, null, 2)
        const fileHandle = await templateDirectoryHandle.getFileHandle('index.json', { create: true })
        const fileStream = await fileHandle.createWritable()

        await fileStream.write({ type: 'write', position: 0, data })

        await fileStream.close()

        // create style.css
        const globalStyleData = `.page {
  --page-width: 210mm;
  --page-height: 297mm;

  --page-padding-top: 20mm;
  --page-padding-bottom: 20mm;
  --page-padding-outer: 15mm;
  --page-padding-inner: 22mm;

  width: var(--page-width);
  height: var(--page-height);
  box-sizing: border-box;
  position: relative;
}

.page-content {
  width: var(--page-width);
  height: var(--page-height);
  padding-top: var(--page-padding-top);
  padding-bottom: var(--page-padding-bottom);
  box-sizing: border-box;
  position: relative;
}

.page-content.left {
  padding-left: var(--page-padding-outer);
  padding-right: var(--page-padding-inner);
}

.page-content.right {
  padding-left: var(--page-padding-inner);
  padding-right: var(--page-padding-outer);
}

[contenteditable] {
  outline: none;
}`
        const globalStyleFileHandle = await templateDirectoryHandle.getFileHandle('style.css', { create: true })
        const globalStyleFileStream = await globalStyleFileHandle.createWritable()
        await globalStyleFileStream.write({ type: 'write', position: 0, data: globalStyleData })
        await globalStyleFileStream.close()

        // create layout.html
        const globalLayoutFileHandle = await templateDirectoryHandle.getFileHandle('layout.html', { create: true })
        const globalLayoutFileStream = await globalLayoutFileHandle.createWritable()
        await globalLayoutFileStream.write({ type: 'write', position: 0, data: '' })
        await globalLayoutFileStream.close()

        await this.createSheet(templateDirectoryHandle)
      },

      async createSheet (templateDirectoryHandle, single = true) {
        const template = this.templates.find((t) => t._id === templateDirectoryHandle.name)
        const db = getFirestore()
        const ref = genRef(db, 'any')
        const pageIds = single
          ? [await this.createPage(templateDirectoryHandle)]
          : [
              await this.createPage(templateDirectoryHandle, 'left'),
              await this.createPage(templateDirectoryHandle, 'right')
            ]
        const sheetsDirectoryHandle = await templateDirectoryHandle.getDirectoryHandle('sheets', { create: true })
        const sheetDirectoryHandle = await sheetsDirectoryHandle.getDirectoryHandle(ref.id, { create: true })

        // create index.json
        const indexData = JSON.stringify({
          order: template?.sheets.length || 0,
          format: template?.printInfo.format || {
            width: 210,
            height: 297,
            units: 'mm'
          },
          pagesIds: pageIds
        }, null, 2)
        const indexFileHandle = await sheetDirectoryHandle.getFileHandle('index.json', { create: true })
        const indexFileStream = await indexFileHandle.createWritable()
        await indexFileStream.write({ type: 'write', position: 0, data: indexData })
        await indexFileStream.close()
      },

      async createPage (templateDirectoryHandle, side = 'right') {
        const template = this.templates.find((t) => t._id === templateDirectoryHandle.name)
        const db = getFirestore()
        const ref = genRef(db, 'any')
        const pagesDirectoryHandle = await templateDirectoryHandle.getDirectoryHandle('pages', { create: true })
        const pageDirectoryHandle = await pagesDirectoryHandle.getDirectoryHandle(ref.id, { create: true })

        // create index.json
        const indexData = JSON.stringify({
          name: '',
          format: template?.printInfo.format || {
            width: 210,
            height: 297,
            units: 'mm'
          },
          side: side,
          colorPrint: true
        }, null, 2)
        const indexFileHandle = await pageDirectoryHandle.getFileHandle('index.json', { create: true })
        const indexFileStream = await indexFileHandle.createWritable()
        await indexFileStream.write({ type: 'write', position: 0, data: indexData })
        await indexFileStream.close()

        // create layout.html
        const layoutData = `
<div class="page">
  <div class="page-content ${side}">

  </div>
</div>`
        const layoutFileHandle = await pageDirectoryHandle.getFileHandle('layout.html', { create: true })
        const layoutFileStream = await layoutFileHandle.createWritable()
        await layoutFileStream.write({ type: 'write', position: 0, data: layoutData })
        await layoutFileStream.close()

        // create style.css
        const styleData = ''
        const styleFileHandle = await pageDirectoryHandle.getFileHandle('style.css', { create: true })
        const styleFileStream = await styleFileHandle.createWritable()
        await styleFileStream.write({ type: 'write', position: 0, data: styleData })
        await styleFileStream.close()

        return ref.id
      },

      async deployLocalTemplate (templateId) {
        const db = getFirestore()
        const templatesRef = collection(db, 'templates')
        const book = templates.find((template) => template._id === templateId)

        await book.toFirestore(templatesRef)
      }
    }
  }
}

Vue.use(LocalTemplates)

export default LocalTemplates

async function verifyPermission (directoryHandle, request) {
  if ((await directoryHandle.queryPermission()) === 'granted') {
    return true
  } else if (request) {
    if ((await directoryHandle.requestPermission({ mode: 'readwrite' })) === 'granted') {
      return true
    }
  } else {
    throw new Error('No user permission')
  }
}

function getRefFromHandle (collectionPath, handle) {
  const db = getFirestore()
  return doc(collection(db, collectionPath), handle.name)
}

async function getDocumentData (directoryHandle) {
  const indexFileHandle = await directoryHandle.getFileHandle('index.json')
  const indexFile = await readFile(indexFileHandle)
  const data = JSON.parse(indexFile)
  return data
}

async function readFile (fileHandle) {
  const file = await fileHandle.getFile()
  const reader = new FileReader()

  return await new Promise((resolve) => {
    reader.readAsText(file, 'UTF-8')
    reader.onloadend = async () => resolve(reader.result)
  })
}

function genRef (parentRef, collectionName) {
  let ref

  do {
    ref = doc(collection(parentRef, collectionName))
  } while (ref.id[0] >= '0' && ref.id[0] <= '9')

  return ref
}

async function getTemplateGlobalLayout (directoryHandle) {
  const layoutFileHandle = await directoryHandle.getFileHandle('layout.html', { create: true })
  const layout = await readFile(layoutFileHandle)
  return layout
}

async function getTemplateGlobalStyles (directoryHandle) {
  const styleFileHandle = await directoryHandle.getFileHandle('style.css', { create: true })
  const style = await readFile(styleFileHandle)
  return style
}
